import { Controller } from "@hotwired/stimulus"

const BASE_URL = "https://api.doorflow.com"

const LANGUAGES = [
  { key: "curl",  label: "cURL"  },
  { key: "ruby",  label: "Ruby"  },
  { key: "swift", label: "Swift" }
]

const SCENARIOS = [
  {
    title: "Admit someone instantly",
    description: "Unlock a door for a person right now — no credential needed.",
    method: "POST",
    endpoint: "/api/3/channels/88/admit_person",
    code: {
      curl: `curl -X POST \\
  "${BASE_URL}/api/3/channels/88/admit_person" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"person_id": 7841}'`,
      ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/3/channels/88/admit_person")
req = Net::HTTP::Post.new(uri, {
  "Authorization" => "Bearer YOUR_ACCESS_TOKEN",
  "Content-Type"  => "application/json"
})
req.body = { person_id: 7841 }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }`,
      swift: `var req = URLRequest(url: URL(string: "${BASE_URL}/api/3/channels/88/admit_person")!)
req.httpMethod = "POST"
req.setValue("Bearer YOUR_ACCESS_TOKEN", forHTTPHeaderField: "Authorization")
req.setValue("application/json", forHTTPHeaderField: "Content-Type")
req.httpBody = try? JSONSerialization.data(withJSONObject: ["person_id": 7841])
URLSession.shared.dataTask(with: req) { data, _, _ in }.resume()`
    },
    statusCode: 202,
    statusLabel: "Accepted",
    responseDescription: "Admission request accepted and queued",
    responseBody: `{
  "admission_request_id": 4923871,
  "status": "pending"
}`,
    note: "Use it when someone needs immediate, one-off access — a forgotten card, a first-day visitor, or a delivery driver at a controlled entry point.",
    docSlug: "admitperson"
  },
  {
    title: "Grant visitor access",
    description: "Time-limited access to specific doors. Auto-revoked at end_time.",
    method: "POST",
    endpoint: "/api/3/reservations",
    code: {
      curl: `curl -X POST \\
  "${BASE_URL}/api/3/reservations" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "person_id": 9203,
    "start_time": "2026-05-08T09:00:00Z",
    "end_time": "2026-05-08T18:00:00Z",
    "channel_ids": [88, 92]
  }'`,
      ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/3/reservations")
req = Net::HTTP::Post.new(uri, {
  "Authorization" => "Bearer YOUR_ACCESS_TOKEN",
  "Content-Type"  => "application/json"
})
req.body = {
  person_id: 9203,
  start_time: "2026-05-08T09:00:00Z",
  end_time:   "2026-05-08T18:00:00Z",
  channel_ids: [88, 92]
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }`,
      swift: `var req = URLRequest(url: URL(string: "${BASE_URL}/api/3/reservations")!)
req.httpMethod = "POST"
req.setValue("Bearer YOUR_ACCESS_TOKEN", forHTTPHeaderField: "Authorization")
req.setValue("application/json", forHTTPHeaderField: "Content-Type")
req.httpBody = try? JSONSerialization.data(withJSONObject: [
  "person_id": 9203,
  "start_time": "2026-05-08T09:00:00Z",
  "end_time": "2026-05-08T18:00:00Z",
  "channel_ids": [88, 92]
])
URLSession.shared.dataTask(with: req) { data, _, _ in }.resume()`
    },
    statusCode: 201,
    statusLabel: "Created",
    responseDescription: "Reservation created successfully",
    responseBody: `{
  "id": 5512,
  "person_id": 9203,
  "start_time": "2026-05-08T09:00:00Z",
  "end_time": "2026-05-08T18:00:00Z",
  "channel_ids": [88, 92],
  "created_at": "2026-05-08T08:30:00Z"
}`,
    note: "Ideal for coworking day passes, client visits, or short-term contractors. Access expires automatically with no cleanup required.",
    docSlug: "createreservation"
  },
  {
    title: "Issue a wallet pass",
    description: "Apple or Google Wallet credential. Send the link — they add it to their wallet.",
    method: "POST",
    endpoint: "/api/3/people/7841/credentials",
    code: {
      curl: `curl -X POST \\
  "${BASE_URL}/api/3/people/7841/credentials" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "person_credential": {
      "credential_type_id": 9,
      "enabled": true
    }
  }'`,
      ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/3/people/7841/credentials")
req = Net::HTTP::Post.new(uri, {
  "Authorization" => "Bearer YOUR_ACCESS_TOKEN",
  "Content-Type"  => "application/json"
})
req.body = {
  person_credential: {
    credential_type_id: 9,
    enabled: true
  }
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }`,
      swift: `var req = URLRequest(url: URL(string: "${BASE_URL}/api/3/people/7841/credentials")!)
req.httpMethod = "POST"
req.setValue("Bearer YOUR_ACCESS_TOKEN", forHTTPHeaderField: "Authorization")
req.setValue("application/json", forHTTPHeaderField: "Content-Type")
req.httpBody = try? JSONSerialization.data(withJSONObject: [
  "person_credential": [
    "credential_type_id": 9,
    "enabled": true
  ]
])
URLSession.shared.dataTask(with: req) { data, _, _ in }.resume()`
    },
    statusCode: 201,
    statusLabel: "Created",
    responseDescription: "PassFlow credential created — share the URL with the recipient",
    responseBody: `{
  "id": "cred_p8x7y6z5",
  "person_id": 7841,
  "label": "PassFlow Mobile Access",
  "status": "invited",
  "url": "https://redeem.passflow.io/passes/5wUXbsX",
  "created_at": "2026-05-08T09:00:02Z"
}`,
    note: "When the person opens the link on their phone, the pass is added directly to Apple or Google Wallet. The credential status updates automatically and triggers a webhook.",
    docSlug: "createcredential"
  },
  {
    title: "Temporary group access",
    description: "Assign a person to groups for a set period — they inherit all access those groups have.",
    method: "POST",
    endpoint: "/api/3/group_reservations",
    code: {
      curl: `curl -X POST \\
  "${BASE_URL}/api/3/group_reservations" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "person_id": 9203,
    "start_time": "2026-05-08T08:00:00Z",
    "end_time": "2026-05-15T18:00:00Z",
    "group_ids": [15]
  }'`,
      ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/3/group_reservations")
req = Net::HTTP::Post.new(uri, {
  "Authorization" => "Bearer YOUR_ACCESS_TOKEN",
  "Content-Type"  => "application/json"
})
req.body = {
  person_id: 9203,
  start_time: "2026-05-08T08:00:00Z",
  end_time:   "2026-05-15T18:00:00Z",
  group_ids: [15]
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }`,
      swift: `var req = URLRequest(url: URL(string: "${BASE_URL}/api/3/group_reservations")!)
req.httpMethod = "POST"
req.setValue("Bearer YOUR_ACCESS_TOKEN", forHTTPHeaderField: "Authorization")
req.setValue("application/json", forHTTPHeaderField: "Content-Type")
req.httpBody = try? JSONSerialization.data(withJSONObject: [
  "person_id": 9203,
  "start_time": "2026-05-08T08:00:00Z",
  "end_time": "2026-05-15T18:00:00Z",
  "group_ids": [15]
])
URLSession.shared.dataTask(with: req) { data, _, _ in }.resume()`
    },
    statusCode: 201,
    statusLabel: "Created",
    responseDescription: "Group reservation created and now active",
    responseBody: `{
  "id": 3301,
  "person_id": 9203,
  "start_time": "2026-05-08T08:00:00Z",
  "end_time": "2026-05-15T18:00:00Z",
  "state": "active",
  "group_ids": [15],
  "created_at": "2026-05-08T07:55:00Z"
}`,
    note: "Great for contractors or temporary staff — grant multi-door access in one call, with automatic expiry at the end date.",
    docSlug: "creategroupreservation"
  },
  {
    title: "Set up door alerts",
    description: "Fire a webhook whenever specific access events happen at specific doors.",
    method: "POST",
    endpoint: "/api/3/notification_rules",
    code: {
      curl: `curl -X POST \\
  "${BASE_URL}/api/3/notification_rules" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Alert: main entrance opened",
    "active": true,
    "call_back_url": "https://yourapp.com/webhooks/doorflow",
    "match_event_codes": [10],
    "match_controller_ids": [88]
  }'`,
      ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/3/notification_rules")
req = Net::HTTP::Post.new(uri, {
  "Authorization" => "Bearer YOUR_ACCESS_TOKEN",
  "Content-Type"  => "application/json"
})
req.body = {
  name: "Alert: main entrance opened",
  active: true,
  call_back_url: "https://yourapp.com/webhooks/doorflow",
  match_event_codes: [10],
  match_controller_ids: [88]
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }`,
      swift: `var req = URLRequest(url: URL(string: "${BASE_URL}/api/3/notification_rules")!)
req.httpMethod = "POST"
req.setValue("Bearer YOUR_ACCESS_TOKEN", forHTTPHeaderField: "Authorization")
req.setValue("application/json", forHTTPHeaderField: "Content-Type")
req.httpBody = try? JSONSerialization.data(withJSONObject: [
  "name": "Alert: main entrance opened",
  "active": true,
  "call_back_url": "https://yourapp.com/webhooks/doorflow",
  "match_event_codes": [10],
  "match_controller_ids": [88]
])
URLSession.shared.dataTask(with: req) { data, _, _ in }.resume()`
    },
    statusCode: 200,
    statusLabel: "OK",
    responseDescription: "Notification rule created and active",
    responseBody: `{
  "id": 441,
  "name": "Alert: main entrance opened",
  "active": true,
  "conditions": [
    { "type": "controller", "controller_ids": [88] }
  ],
  "events": [
    { "code": 10, "label": "Access granted" }
  ],
  "actions": [
    { "type": "webhook", "url": "https://yourapp.com/webhooks/doorflow" }
  ]
}`,
    note: "Filter by event type (access granted, denied, door held open) and specific doors. Build Slack alerts, audit logs, or security automations on top.",
    docSlug: "createnotificationrule"
  }
]

export default class extends Controller {
  static targets = [
    "scenarioRow",
    "code",
    "noteText",
    "docLink",
    "languageTab"
  ]

  static values = {
    scenario: { type: Number, default: 0 },
    language: { type: String, default: "curl" }
  }

  connect() {
    this.render()
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.scenarioIndex)
    if (index === this.scenarioValue) return
    this.scenarioValue = index
    this.render()
  }

  selectLanguage(event) {
    const lang = event.currentTarget.dataset.language
    if (lang === this.languageValue) return
    this.languageValue = lang
    this.render()
  }

  copy(event) {
    const text = SCENARIOS[this.scenarioValue].code[this.languageValue]
    navigator.clipboard.writeText(text).then(() => {
      const btn = event.currentTarget
      const icon = btn.querySelector("svg")
      if (icon) {
        icon.classList.add("text-green-500")
        setTimeout(() => icon.classList.remove("text-green-500"), 1500)
      }
    })
  }

  render() {
    const s = SCENARIOS[this.scenarioValue]

    this.scenarioRowTargets.forEach((row, i) => {
      const active = i === this.scenarioValue
      row.classList.toggle("bg-gray-50", !active)
      row.style.borderLeftColor = active ? "#0e2653" : "transparent"
    })

    this.languageTabTargets.forEach(tab => {
      const active = tab.dataset.language === this.languageValue
      tab.className = active
        ? "px-4 py-3 text-sm font-medium transition-colors text-gray-900 bg-white"
        : "px-4 py-3 text-sm font-medium transition-colors border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
    })

    this.codeTarget.textContent = s.code[this.languageValue]
    this.noteTextTarget.textContent = s.note
    this.docLinkTarget.href = `/api-ref/doorflow/${s.docSlug}`
  }
}
